import React, { useContext } from 'react';
import { BackgroundContext, LinkContext } from "../../internal";
import { InternalText, useTextColor } from "../../internal/InternalText";
import { hasSufficientContrast } from "../../utils";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Used to display some text as a semantic heading
 *
 * ## Accessibility ♿
 * - Use this only for **headings** or you will cause accessibility issues. Use the Text component for non-heading content.
 * - Carefully select the level prop in order to not cause accessibility issues
 *   - Only have a level 1 heading per page
 *   - Heading levels should be in order on the page
 *   - Don't skip levels
 * - Do not use Heading for "sub-headings" directly underneath main headings.  Use Text for subheadings and link the Heading to the subheading using aria-describedby.
 * - See [this documentation](https://applyboard.atlassian.net/wiki/spaces/UE/pages/2681012586/Heading+Accessibility) for more accessibility information on headings
 * - Accessibility features:
 *   - The level prop does not result in any visual changes.  This is meant to de-couple the semantic level from the visual appearance, as they are often different.  Ideally this will encourage developers to pause and give some thought to what the right semantic level should be for the heading.
 */
export var Heading = /*#__PURE__*/React.forwardRef(function Heading(_ref, ref) {
  var _ref$align = _ref.align,
    align = _ref$align === void 0 ? 'start' : _ref$align,
    children = _ref.children,
    _ref$contrast = _ref.contrast,
    contrast = _ref$contrast === void 0 ? 'high' : _ref$contrast,
    id = _ref.id,
    _ref$intent = _ref.intent,
    intent = _ref$intent === void 0 ? 'secondary' : _ref$intent,
    level = _ref.level,
    lineClamp = _ref.lineClamp,
    m = _ref.m,
    mb = _ref.mb,
    ml = _ref.ml,
    mr = _ref.mr,
    mt = _ref.mt,
    mx = _ref.mx,
    my = _ref.my,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'titleM' : _ref$variant;
  if (typeof level === 'undefined' || level === null) {
    throw new Error('The level prop is required and you can cause accessibility issues if you pick the wrong level. Please read this document: https://applyboard.atlassian.net/wiki/spaces/UE/pages/2681012586/Heading+Best+Practices');
  }
  if (typeof level !== 'number') {
    throw new Error('The level prop must be a number');
  }
  if (level < 1 || level > 6) {
    throw new Error('The level prop must be between 1 and 6 inclusive');
  }
  var _useContext = useContext(BackgroundContext),
    backgroundColor = _useContext.backgroundColor,
    isBackgroundGradient = _useContext.isBackgroundGradient;
  var linkContext = useContext(LinkContext);
  var colorUtil = useTextColor(intent, !!linkContext && isBackgroundGradient ? 'inverted' : contrast);
  if (intent === 'secondary' && !hasSufficientContrast(colorUtil(), backgroundColor)) {
    // NOTE: if you see this error and think this is a false positive please reach out to UIE
    // There is a follow up ticket UIE-2223 to implement BackgroundContext in all components
    throw new Error('There is an accessibility issue; there is not enough contrast between the Heading and background color.');
  }
  return _jsx(InternalText, {
    align: align,
    as: "h".concat(level),
    contrast: contrast,
    id: id,
    intent: intent,
    lineClamp: lineClamp,
    m: m,
    mb: mb,
    ml: ml,
    mr: mr,
    mt: mt,
    mx: mx,
    my: my,
    ref: ref,
    variant: variant,
    children: children
  });
});